import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { sameKeys } from '@client/components/_core/layout/sameKeys';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Alerts } from '@client/components/_ui/display/Alerts';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { ComplaintFilters } from '@client/routes/~console.$orgId/~compliance/~complaints/ComplaintFilters';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DETAIL_RECORD_QUERY_OPTIONS } from '@client/trpc/query-options';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Tag } from 'antd';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/compliance/incidents/details/$id/manage/complaints')({
    staticData: { presentation: 'overlay' },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <IncidentComplaintManage />
        </PageController>
    );
}

type QueryParams = RouterInputs['complaint']['listComplaints'];
type ComplaintOrderBy = NonNullable<QueryParams['orderBy']>;
type ComplaintRow = RouterOutputs['complaint']['listComplaints']['rows'][number];

const columnHelper = createColumnHelper<ComplaintRow, ComplaintOrderBy>();

function IncidentComplaintManage() {
    const orgId = useOrgId();
    const { id } = Route.useParams();
    const { trpcUtils } = useTrpc();

    // Query incident data directly to ensure it's available when accessing this route directly
    const { data: incidentData, isPending: isIncidentLoading } = trpc.incident.getIncident.useQuery(
        { id },
        DETAIL_RECORD_QUERY_OPTIONS,
    );

    const { data: complaintIds } = trpc.incident.getIncidentComplaintIds.useQuery({
        id,
    });
    const { mutate, isPending: isPendingUpdateIncidentComplaints } =
        trpc.incident.updateIncidentComplaints.useMutation();
    const navigate = useNavigate();

    const [selectedKeys, setSelectedKeys] = useState<bigint[]>([]);
    const [queryParams, setQueryParams] = useState<QueryParams>({
        limit: DEFAULT_PAGE_SIZE,
        orderBy: [{ id: 'desc' }],
    });

    const { data, isPending: isPendingListComplaints, refetch } = trpc.complaint.listComplaints.useQuery(queryParams);

    const tableProps = usePrismaTable<ComplaintRow, QueryParams['where'], ComplaintOrderBy>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPendingListComplaints || isPendingUpdateIncidentComplaints || isIncidentLoading,
        columns: [
            columnHelper.display({
                id: 'indicator',
                header: '',
                width: 40,
                fixed: 'left',
                align: 'center',
                hideable: false,
                cell: ({ row }) => (
                    <Alerts id={row.id} entity="complaint" indicatorColor={row.indicatorColor} key={row.id} />
                ),
            }),
            columnHelper.accessor('id', {
                id: 'id',
                header: 'Complaint number',
                sortable: 'id',
                cell: ({ row }) => String(row.id),
                fixed: 'left',
                hideable: false,
            }),
            columnHelper.accessor('issues', {
                id: 'issues',
                header: 'Issues',
                sortable: 'issues',
                cell: ({ row }) =>
                    ((row.issues as string[] | undefined) || []).map((item: string, i: number) => (
                        <Tag variant="outlined" color="volcano" key={`${item}-${i}`}>
                            {item}
                        </Tag>
                    )),
            }),
            columnHelper.display({
                id: 'owner',
                header: 'Owner',
                cell: ({ row }) => (
                    <User userId={row.Owner?.userId} name={row.Owner?.fullName} photoUrl={row.Owner?.photoUrl} />
                ),
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                cell: ({ value }) => value && <Tag variant="outlined">{value}</Tag>,
            }),
            columnHelper.actions({
                id: 'actions',
                fixed: 'right',
                cell: ({ row }) => (
                    <Button.OpenInNewTab
                        preload={false}
                        to="/console/$orgId/compliance/complaints/details/$id/manage"
                        params={{
                            orgId,
                            id: row.id,
                        }}
                        tooltip="Open in new tab"
                    />
                ),
            }),
        ],
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        emptyState: { noRecordsTitle: 'No complaints yet' },
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'incident-complaint-manage-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: refetch,
        },
        rowSelection: {
            enabled: true,
            selectedRowKeys: selectedKeys,
            onChange(selectedRowKeys) {
                setSelectedKeys(selectedRowKeys.map(BigInt));
            },
        },
    });

    useEffect(() => {
        if (complaintIds) {
            setSelectedKeys(complaintIds);
        }
    }, [complaintIds]);

    return (
        <OverlayPage
            title="Select complaints"
            size="large"
            isDirty={() => complaintIds !== undefined && !sameKeys(selectedKeys, complaintIds)}
            afterClose={() => {
                void trpcUtils.complaint.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose, requestClose }) => (
                <>
                    <Button type="secondary" onClick={requestClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        loading={isPendingUpdateIncidentComplaints}
                        onClick={() => {
                            const hide = message.loading('Saving...', 0);

                            mutate(
                                {
                                    id,
                                    set: {
                                        complaintIds: selectedKeys,
                                    },
                                },
                                {
                                    async onSuccess() {
                                        hide();
                                        message.success('Saved.');
                                        onClose();
                                    },
                                    onError(error) {
                                        hide();
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        }}
                    >
                        Save
                    </Button>
                </>
            )}
        >
            <TableToolbar
                variant="enhanced"
                filters={
                    <ComplaintFilters onChange={(where) => setQueryParams((prev) => ({ ...prev, where, offset: 0 }))} />
                }
                actions={
                    incidentData && (
                        <Button.Add.Link
                            to="/console/$orgId/compliance/complaints/add"
                            params={{ orgId }}
                            search={{
                                incident: {
                                    incidentId: incidentData.id,
                                    handlingPartyIds:
                                        incidentData.HandlingParties?.map((hp) => hp.handlingPartyId) || [],
                                    ownerId: incidentData.ownerId,
                                    products: incidentData.products,
                                    policyNo: incidentData.policyNo || undefined,
                                    externalClaimId: incidentData.externalClaimId || undefined,
                                },
                            }}
                            variant="primary"
                            label="New complaint"
                        />
                    )
                }
            />
            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick() {
                        setSelectedKeys((prev) =>
                            prev.includes(id) ? prev.filter((key) => key !== id) : [...prev, id],
                        );
                    },
                })}
            />
        </OverlayPage>
    );
}
